import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { defaultCountries, parseCountry, PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import FieldError from './Components/FieldError';
import Label from './Components/Label';
import TextInput from './Components/TextInput';

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
    }
}

interface Props {
    locale: string;
    defaultCountry?: string;
    countries: Record<string, string>;
    registrationAttribution: Record<string, unknown>;
    termsUrl: string;
    privacyUrl: string;
    csrfToken: string;
    registerUrl: string;
}

type AccountType = 'company' | 'private';

const COUNTRIES_REQUIRING_BUSINESS_ID = ['EE', 'FI', 'SE'];

const BUSINESS_ID_PLACEHOLDER: Record<string, string> = {
    SE: '123456-1234',
    EE: '12345678',
};
const DEFAULT_BUSINESS_ID_PLACEHOLDER = '1234567-8';

export const RegistrationForm = ({
    locale,
    defaultCountry,
    countries,
    registrationAttribution,
    termsUrl,
    privacyUrl,
    csrfToken,
    registerUrl,
}: Props) => {
    const { t } = useTranslation();

    const countryDisplayNames = useMemo(
        () => new Intl.DisplayNames([locale, 'en'], { type: 'region' }),
        [locale],
    );

    const localizedPhoneCountries = useMemo(
        () =>
            defaultCountries
                .map((country) => {
                    const { iso2 } = parseCountry(country);
                    const localizedName = countryDisplayNames.of(iso2.toUpperCase());
                    return (
                        localizedName ? [localizedName, ...country.slice(1)] : country
                    ) as typeof country;
                })
                .sort((a, b) => a[0].localeCompare(b[0], locale)),
        [countryDisplayNames, locale],
    );

    const [signupToken] = useState<string | null>(() => {
        const fragment = new URLSearchParams(window.location.hash.slice(1));
        return fragment.get('signup') ?? window.history.state?.shipitSignupToken ?? null;
    });

    useEffect(() => {
        if (
            signupToken !== null &&
            new URLSearchParams(window.location.hash.slice(1)).has('signup')
        ) {
            window.history.replaceState(
                { ...window.history.state, shipitSignupToken: signupToken },
                '',
                window.location.pathname + window.location.search,
            );
        }
    }, [signupToken]);

    const [type, setType] = useState<AccountType>('company');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [name, setName] = useState('');
    const [businessId, setBusinessId] = useState('');
    const [eori, setEori] = useState('');
    const [vatNumber, setVatNumber] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [postcode, setPostcode] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState(defaultCountry ?? 'FI');
    const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
    const [termsOfServiceConsent, setTermsOfServiceConsent] = useState(false);
    const [honeypot, setHoneypot] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [generalError, setGeneralError] = useState<string | null>(null);

    const formRef = useRef<HTMLFormElement>(null);
    const generalErrorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (generalError) {
            generalErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [generalError]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            formRef.current
                ?.querySelector('[data-field-error]')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errors]);

    const isCompany = type === 'company';
    const isSwedishCompany = isCompany && country === 'SE';
    const businessIdRequired = isCompany && COUNTRIES_REQUIRING_BUSINESS_ID.includes(country);

    const validate = (): Record<string, string> => {
        const newErrors: Record<string, string> = {};

        if (!email) {
            newErrors.email = t('required-field');
        } else {
            const emailEl = document.createElement('input');
            emailEl.type = 'email';
            emailEl.value = email;
            if (!emailEl.checkValidity()) newErrors.email = t('invalid-email');
        }

        if (!password) {
            newErrors.password = t('required-field');
        } else if (password.length < 8) {
            newErrors.password = t('password-too-short');
        }

        if (!password2) newErrors.password2 = t('required-field');
        else if (password2 !== password) newErrors.password2 = t('password-mismatch');

        if (!name) newErrors.name = t('required-field');
        if (isCompany && !contactPerson) newErrors.contactPerson = t('required-field');
        if (businessIdRequired && !businessId) newErrors.businessId = t('required-field');

        if (!phone) {
            newErrors.phone = t('required-field');
        } else if (phone.replace(/\D/g, '').length < 7) {
            newErrors.phone = t('invalid-phone');
        }

        if (!address) newErrors.address = t('required-field');
        if (!postcode) newErrors.postcode = t('required-field');
        if (!city) newErrors.city = t('required-field');
        if (!termsOfServiceConsent) newErrors.termsOfServiceConsent = t('terms-required');

        return newErrors;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const clientErrors = validate();
        if (Object.keys(clientErrors).length > 0) {
            setErrors(clientErrors);
            return;
        }

        setSubmitting(true);
        setErrors({});
        setGeneralError(null);

        try {
            const response = await fetch(registerUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify({
                    type,
                    email,
                    password,
                    password2,
                    name,
                    businessId: businessId || null,
                    ...(isSwedishCompany
                        ? {}
                        : {
                              eori: eori || null,
                              vatNumber: vatNumber || null,
                          }),
                    contactPerson: contactPerson || null,
                    phone,
                    address,
                    postcode,
                    city,
                    country,
                    subscribeNewsletter,
                    termsOfServiceConsent,
                    registrationAttribution: registrationAttribution ?? {},
                    ...(signupToken !== null ? { signupToken } : {}),
                    _honeypot: honeypot,
                }),
            });

            if (response.status === 419) {
                setGeneralError(t('session-expired-error'));
                return;
            }

            if (response.status === 429) {
                setGeneralError(t('rate-limit-error'));
                return;
            }

            let data: {
                redirect_url?: string;
                datalayer_event?: Record<string, unknown>;
                errors?: Record<string, string[]>;
                error_key?: string;
            };
            try {
                data = await response.json();
            } catch {
                setGeneralError(t('general-error'));
                return;
            }

            if (!response.ok) {
                if (data.errors?.signupToken) {
                    setGeneralError(t('signup-link-error'));
                } else if (data.errors && Object.keys(data.errors).length > 0) {
                    setErrors(
                        Object.fromEntries(
                            Object.entries(data.errors).map(([k, v]) => [k, v[0] ?? '']),
                        ),
                    );
                } else {
                    setGeneralError(data.error_key ? t(data.error_key) : t('general-error'));
                }
                return;
            }

            if (data.datalayer_event && typeof window.dataLayer !== 'undefined') {
                window.dataLayer.push(data.datalayer_event);
            }

            window.location.href = data.redirect_url ?? '/';
        } catch {
            setGeneralError(t('general-error'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-screen-xl px-6 py-6 lg:px-12 lg:py-12">
            <div className="max-w-screen-lg">
                <div className="relative rounded border border-blue-gray bg-white">
                    <div className="border-b border-blue-gray px-8 py-6 text-xl font-medium">
                        {t('title')}
                    </div>

                    <form ref={formRef} onSubmit={handleSubmit} noValidate>
                        {/* Honeypot — visually hidden, must be included in submit payload */}
                        <input
                            type="text"
                            aria-hidden="true"
                            tabIndex={-1}
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                            className="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
                            autoComplete="off"
                        />

                        {generalError && (
                            <div
                                ref={generalErrorRef}
                                className="mx-8 mt-6 rounded border border-red bg-red-light px-4 py-3 text-sm text-red"
                            >
                                {generalError}
                            </div>
                        )}

                        <div className="px-8 py-6">
                            <div>
                                <Label required>{t('type')}</Label>
                                <div className="flex gap-6">
                                    <label className="flex cursor-pointer items-center">
                                        <input
                                            type="radio"
                                            value="company"
                                            checked={type === 'company'}
                                            onChange={() => setType('company')}
                                            className="h-4 w-4 border-gray focus:ring-petrol"
                                        />
                                        <span className="ml-2 text-sm">{t('company')}</span>
                                    </label>
                                    <label className="flex cursor-pointer items-center">
                                        <input
                                            type="radio"
                                            value="private"
                                            checked={type === 'private'}
                                            onChange={() => setType('private')}
                                            className="h-4 w-4 border-gray focus:ring-petrol"
                                        />
                                        <span className="ml-2 text-sm">{t('private')}</span>
                                    </label>
                                </div>
                                <FieldError message={errors.type} />
                            </div>

                            <div
                                className={
                                    isSwedishCompany
                                        ? 'mt-8 space-y-8'
                                        : 'mt-4 grid grid-cols-6 gap-4'
                                }
                            >
                                <section
                                    aria-labelledby={
                                        isSwedishCompany
                                            ? 'registration-account-heading'
                                            : undefined
                                    }
                                    className={isSwedishCompany ? undefined : 'contents'}
                                >
                                    {isSwedishCompany ? (
                                        <h2
                                            id="registration-account-heading"
                                            className="mb-4 text-xs font-semibold uppercase text-gray-dark"
                                        >
                                            {t('section-account')}
                                        </h2>
                                    ) : null}
                                    <div
                                        className={
                                            isSwedishCompany ? 'grid grid-cols-6 gap-4' : 'contents'
                                        }
                                    >
                                        <div
                                            className={
                                                isSwedishCompany
                                                    ? 'col-span-6'
                                                    : 'col-span-6 md:col-span-3'
                                            }
                                        >
                                            <Label htmlFor="email" required>
                                                {t('email')}
                                            </Label>
                                            <TextInput
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                error={errors.email}
                                                autoComplete="email"
                                            />
                                            <FieldError message={errors.email} />
                                        </div>

                                        <div className="col-span-6 md:col-span-3">
                                            <Label htmlFor="password" required>
                                                {t('password')}
                                            </Label>
                                            <TextInput
                                                id="password"
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                error={errors.password}
                                                autoComplete="new-password"
                                                minLength={8}
                                            />
                                            <FieldError message={errors.password} />
                                        </div>

                                        <div className="col-span-6 md:col-span-3">
                                            <Label htmlFor="password2" required>
                                                {t('password-confirmation')}
                                            </Label>
                                            <TextInput
                                                id="password2"
                                                type="password"
                                                value={password2}
                                                onChange={(e) => setPassword2(e.target.value)}
                                                error={errors.password2}
                                                autoComplete="new-password"
                                                minLength={8}
                                            />
                                            <FieldError message={errors.password2} />
                                        </div>
                                    </div>
                                </section>

                                <section
                                    aria-labelledby={
                                        isSwedishCompany
                                            ? 'registration-details-heading'
                                            : undefined
                                    }
                                    className={isSwedishCompany ? undefined : 'contents'}
                                >
                                    {isSwedishCompany ? (
                                        <h2
                                            id="registration-details-heading"
                                            className="mb-4 text-xs font-semibold uppercase text-gray-dark"
                                        >
                                            {t('section-company')}
                                        </h2>
                                    ) : null}
                                    <div
                                        className={
                                            isSwedishCompany ? 'grid grid-cols-6 gap-4' : 'contents'
                                        }
                                    >
                                        {isCompany ? (
                                            <>
                                                <div className="col-span-6 md:col-span-3">
                                                    <Label htmlFor="name" required>
                                                        {t('company-name')}
                                                    </Label>
                                                    <TextInput
                                                        id="name"
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                        error={errors.name}
                                                        autoComplete="organization"
                                                        maxLength={255}
                                                    />
                                                    <FieldError message={errors.name} />
                                                </div>

                                                <div className="col-span-6 md:col-span-3">
                                                    <Label
                                                        htmlFor="businessId"
                                                        required={businessIdRequired}
                                                    >
                                                        {t('business-id')}
                                                    </Label>
                                                    <TextInput
                                                        id="businessId"
                                                        value={businessId}
                                                        onChange={(e) =>
                                                            setBusinessId(e.target.value)
                                                        }
                                                        placeholder={
                                                            BUSINESS_ID_PLACEHOLDER[country] ??
                                                            DEFAULT_BUSINESS_ID_PLACEHOLDER
                                                        }
                                                        error={errors.businessId}
                                                    />
                                                    <FieldError message={errors.businessId} />
                                                </div>

                                                <div
                                                    className={
                                                        isSwedishCompany
                                                            ? 'col-span-6'
                                                            : 'col-span-6 md:col-span-3'
                                                    }
                                                >
                                                    <Label htmlFor="contactPerson" required>
                                                        {t('contact-person')}
                                                    </Label>
                                                    <TextInput
                                                        id="contactPerson"
                                                        value={contactPerson}
                                                        onChange={(e) =>
                                                            setContactPerson(e.target.value)
                                                        }
                                                        error={errors.contactPerson}
                                                        autoComplete="name"
                                                        maxLength={255}
                                                    />
                                                    <FieldError message={errors.contactPerson} />
                                                </div>

                                                {!isSwedishCompany ? (
                                                    <>
                                                        <div className="col-span-6 md:col-span-3">
                                                            <Label htmlFor="eori">
                                                                {t('eori')}
                                                            </Label>
                                                            <TextInput
                                                                id="eori"
                                                                value={eori}
                                                                onChange={(e) =>
                                                                    setEori(e.target.value)
                                                                }
                                                                error={errors.eori}
                                                                maxLength={128}
                                                            />
                                                            <FieldError message={errors.eori} />
                                                        </div>

                                                        <div className="col-span-6 md:col-span-3">
                                                            <Label htmlFor="vatNumber">
                                                                {t('vat-number')}
                                                            </Label>
                                                            <TextInput
                                                                id="vatNumber"
                                                                value={vatNumber}
                                                                onChange={(e) =>
                                                                    setVatNumber(e.target.value)
                                                                }
                                                                error={errors.vatNumber}
                                                                maxLength={128}
                                                            />
                                                            <FieldError
                                                                message={errors.vatNumber}
                                                            />
                                                        </div>
                                                    </>
                                                ) : null}
                                            </>
                                        ) : (
                                            <div className="col-span-6 md:col-span-3">
                                                <Label htmlFor="name" required>
                                                    {t('name')}
                                                </Label>
                                                <TextInput
                                                    id="name"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    error={errors.name}
                                                    autoComplete="name"
                                                    maxLength={255}
                                                />
                                                <FieldError message={errors.name} />
                                            </div>
                                        )}

                                        <div
                                            className={
                                                isSwedishCompany
                                                    ? 'col-span-6'
                                                    : 'col-span-6 md:col-span-3'
                                            }
                                        >
                                            <Label htmlFor="phone" required>
                                                {t('phone')}
                                            </Label>
                                            <PhoneInput
                                                defaultCountry={
                                                    defaultCountry?.toLowerCase() ?? 'fi'
                                                }
                                                countries={localizedPhoneCountries}
                                                value={phone}
                                                onChange={setPhone}
                                                inputProps={{ id: 'phone' }}
                                                className={`w-full rounded border ${errors.phone ? 'border-red' : 'border-gray'}`}
                                                inputClassName="w-full border-none bg-transparent py-2.5 px-3 text-sm leading-5 focus:outline-none focus:ring-0"
                                                countrySelectorStyleProps={{
                                                    buttonClassName:
                                                        'h-full border-r border-gray px-3 transition duration-200',
                                                }}
                                            />
                                            <FieldError message={errors.phone} />
                                        </div>
                                    </div>
                                </section>

                                <section
                                    aria-labelledby={
                                        isSwedishCompany
                                            ? 'registration-address-heading'
                                            : undefined
                                    }
                                    className={isSwedishCompany ? undefined : 'contents'}
                                >
                                    {isSwedishCompany ? (
                                        <h2
                                            id="registration-address-heading"
                                            className="mb-4 text-xs font-semibold uppercase text-gray-dark"
                                        >
                                            {t('section-address')}
                                        </h2>
                                    ) : null}
                                    <div
                                        className={
                                            isSwedishCompany ? 'grid grid-cols-6 gap-4' : 'contents'
                                        }
                                    >
                                        <div
                                            className={
                                                isSwedishCompany
                                                    ? 'col-span-6'
                                                    : 'col-span-6 md:col-span-3'
                                            }
                                        >
                                            <Label htmlFor="address" required>
                                                {t('address')}
                                            </Label>
                                            <TextInput
                                                id="address"
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                                error={errors.address}
                                                autoComplete="street-address"
                                                maxLength={255}
                                            />
                                            <FieldError message={errors.address} />
                                        </div>

                                        <div className="col-span-6 md:col-span-3">
                                            <Label htmlFor="postcode" required>
                                                {t('postal-code')}
                                            </Label>
                                            <TextInput
                                                id="postcode"
                                                value={postcode}
                                                onChange={(e) => setPostcode(e.target.value)}
                                                error={errors.postcode}
                                                autoComplete="postal-code"
                                                maxLength={10}
                                            />
                                            <FieldError message={errors.postcode} />
                                        </div>

                                        <div className="col-span-6 md:col-span-3">
                                            <Label htmlFor="city" required>
                                                {t('city')}
                                            </Label>
                                            <TextInput
                                                id="city"
                                                value={city}
                                                onChange={(e) => setCity(e.target.value)}
                                                error={errors.city}
                                                autoComplete="address-level2"
                                                maxLength={255}
                                            />
                                            <FieldError message={errors.city} />
                                        </div>

                                        <div
                                            className={
                                                isSwedishCompany
                                                    ? 'col-span-6'
                                                    : 'col-span-6 md:col-span-3'
                                            }
                                        >
                                            <Label htmlFor="country" required>
                                                {t('country')}
                                            </Label>
                                            <select
                                                id="country"
                                                value={country}
                                                onChange={(e) => setCountry(e.target.value)}
                                                className={`block w-full rounded border px-3 py-2.5 text-sm leading-5 transition duration-200 focus:border-gray focus:outline-none focus:ring-0 ${
                                                    errors.country
                                                        ? 'border-red bg-red-light'
                                                        : 'border-gray bg-white'
                                                }`}
                                            >
                                                {Object.keys(countries ?? {})
                                                    .map((code) => ({
                                                        code,
                                                        label: countryDisplayNames.of(code) ?? code,
                                                    }))
                                                    .sort((a, b) =>
                                                        a.label.localeCompare(b.label, locale),
                                                    )
                                                    .map(({ code, label }) => (
                                                        <option key={code} value={code}>
                                                            {label}
                                                        </option>
                                                    ))}
                                            </select>
                                            <FieldError message={errors.country} />
                                        </div>
                                    </div>
                                </section>
                            </div>

                            <div className="grid grid-cols-6 gap-4">
                                <div className="col-span-6 pt-6">
                                    <div className="flex items-baseline space-x-2">
                                        <input
                                            type="checkbox"
                                            id="terms"
                                            checked={termsOfServiceConsent}
                                            onChange={(e) =>
                                                setTermsOfServiceConsent(e.target.checked)
                                            }
                                            className="h-4 w-4 rounded border-gray focus:ring-petrol"
                                        />
                                        <label htmlFor="terms" className="text-sm text-gray-dark">
                                            <Trans
                                                i18nKey="terms-and-conditions"
                                                components={{
                                                    termsLink: (
                                                        <a
                                                            href={termsUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-semibold text-blue"
                                                        />
                                                    ),
                                                    privacyLink: (
                                                        <a
                                                            href={privacyUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-semibold text-blue"
                                                        />
                                                    ),
                                                }}
                                            />
                                            {errors.termsOfServiceConsent && (
                                                <p className="mt-3 text-sm text-red">
                                                    {errors.termsOfServiceConsent}
                                                </p>
                                            )}
                                        </label>
                                    </div>
                                </div>

                                {/* Newsletter consent */}
                                <div className="col-span-6 flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="newsletter"
                                        checked={subscribeNewsletter}
                                        onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                                        className="h-4 w-4 rounded border-gray focus:ring-petrol"
                                    />
                                    <label htmlFor="newsletter" className="text-sm text-gray-dark">
                                        {t('newsletter-consent')}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end border-t border-blue-gray px-8 py-6">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn btn-solid-petrol btn-sm"
                            >
                                {t('create-and-login')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
